import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { RadioGroup, RadioGroupItem } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { TrendingUp } from 'lucide-react'
import type { SkillInput, SavedSkill } from '@creanizer/types'

interface SkillFormEvolutionProps {
  input: SkillInput
  onChange: (input: SkillInput) => void
  savedSkills: SavedSkill[]
  selectedBaseSkillId: string
  setSelectedBaseSkillId: (id: string) => void
}

export function SkillFormEvolution({
  input,
  onChange,
  savedSkills,
  selectedBaseSkillId,
  setSelectedBaseSkillId
}: SkillFormEvolutionProps) {
  const generateSkillContext = (skill: SavedSkill) => {
    return JSON.stringify({
      name: skill.result.basicInfo.name,
      type: skill.result.basicInfo.type,
      tier: skill.result.basicInfo.tier,
      level: skill.result.basicInfo.level,
      description: skill.result.basicInfo.description,
      effects: skill.result.effects,
      mechanics: skill.result.mechanics
    })
  }
  
  return (
    <div className="space-y-6">
      <Alert>
        <TrendingUp className="h-4 w-4" />
        <AlertDescription>
          既存のスキルを進化・強化させた上位版を生成します。
          基礎スキルの特徴を継承しつつ、より強力で洗練されたスキルが作成されます。
        </AlertDescription>
      </Alert>
      
      {savedSkills.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>
            進化元となるスキルがありません。先に標準モードでスキルを生成してください。
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="space-y-2">
            <Label>進化元スキル（必須）</Label>
            <Select
              value={selectedBaseSkillId || ''}
              onValueChange={(value) => {
                const skill = savedSkills.find(s => s.id === value)
                if (skill) {
                  const context = generateSkillContext(skill)
                  onChange({ ...input, baseSkillContext: context })
                  setSelectedBaseSkillId(value)
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="進化元のスキルを選択..." />
              </SelectTrigger>
              <SelectContent>
                {savedSkills.map((skill) => (
                  <SelectItem key={skill.id} value={skill.id}>
                    {skill.result.basicInfo.name} (Lv.{skill.result.basicInfo.level})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>進化の方向性</Label>
            <RadioGroup
              value={input.evolutionPath || 'power'}
              onValueChange={(value) => onChange({ ...input, evolutionPath: value })}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="power" id="power" />
                <Label htmlFor="power">威力強化（より強力に）</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="efficiency" id="efficiency" />
                <Label htmlFor="efficiency">効率化（コスト削減・高速化）</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="versatility" id="versatility" />
                <Label htmlFor="versatility">多機能化（新効果追加）</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specialization" id="specialization" />
                <Label htmlFor="specialization">特化（特定面の極限強化）</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="awakening" id="awakening" />
                <Label htmlFor="awakening">覚醒（隠された真の力）</Label>
              </div>
            </RadioGroup>
          </div>
          
          {selectedBaseSkillId && (
            <Alert>
              <AlertDescription>
                選択したスキルの上位版が生成されます。
                基礎スキルの特性を保ちながら、指定した方向性で進化します。
              </AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  )
}